import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { PrivacyContact, UserProfile } from '@flugwettrennen/shared';
import { api } from './api';
import { setLanguage as setI18nLanguage } from './i18n';

interface AuthState {
  user: UserProfile | null;
  inviteOnly: boolean;
  privacyContact: PrivacyContact;
  loading: boolean;
  setUser: (u: UserProfile | null) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [inviteOnly, setInviteOnly] = useState(true);
  const [privacyContact, setPrivacyContact] = useState<PrivacyContact>({
    name: '',
    email: '',
    address: null,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.auth
      .me()
      .then((r) => {
        setUser(r.user);
        setInviteOnly(r.inviteOnly);
        if (r.privacyContact) setPrivacyContact(r.privacyContact);
        if (r.user) setI18nLanguage(r.user.language);
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  async function logout() {
    await api.auth.logout().catch(() => undefined);
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, inviteOnly, privacyContact, loading, setUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth außerhalb des AuthProvider');
  return ctx;
}
